import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FlaskConical, Play } from "lucide-react";
import { BridgeApi } from "../lib/api";
import { qk } from "../lib/query";
import { PageBody, PageHeader } from "../components/PageScaffold";
import { Button, Pill, Panel, PanelHeader } from "../components/primitives";
import { PlatformBadge, AclBadge, PersonaBadge } from "../components/badges";
import type { InboundACL, Platform } from "../lib/types";

const PLATFORMS: Platform[] = ["whatsapp", "gchat", "telegram", "asana", "internal"];
const CHAT_KINDS = ["dm", "group", "newsletter", "broadcast", "unknown"];

export default function Simulator() {
  const { data: cfg } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });
  const [platform, setPlatform] = useState<Platform>("whatsapp");
  const [chatKind, setChatKind] = useState("dm");
  const [chatJid, setChatJid] = useState("");
  const [senderJid, setSenderJid] = useState("");
  const [text, setText] = useState("");

  const simulate = useMutation({
    mutationFn: () =>
      BridgeApi.simulate({
        platform,
        chat_jid: chatJid,
        chat_kind: chatKind,
        sender_jid: senderJid || undefined,
        text: text || undefined,
      }),
  });

  const result = simulate.data;

  return (
    <>
      <PageHeader title="Simulator" icon={FlaskConical} subtitle="Dry-run route matching against a fake inbound event." />
      <PageBody className="space-y-4 max-w-3xl">
        <Panel>
          <PanelHeader
            title="Event"
            subtitle="Fields match agent-bridge's EmitEvent."
            actions={
              <Button size="sm" onClick={() => simulate.mutate()} loading={simulate.isPending}>
                <Play className="size-3.5" />
                Run match
              </Button>
            }
          />
          <div className="p-4 grid grid-cols-2 gap-3">
            <Field label="Platform">
              <select value={platform} onChange={(e) => setPlatform(e.target.value as Platform)} className="w-full">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Chat kind">
              <select value={chatKind} onChange={(e) => setChatKind(e.target.value)} className="w-full">
                {CHAT_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </Field>
            <Field label="Chat JID">
              <input value={chatJid} onChange={(e) => setChatJid(e.target.value)} className="w-full font-mono text-xs" />
            </Field>
            <Field label="Sender JID (optional)">
              <input value={senderJid} onChange={(e) => setSenderJid(e.target.value)} className="w-full font-mono text-xs" />
            </Field>
            <div className="col-span-2">
              <Field label="Text">
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="w-full" />
              </Field>
            </div>
          </div>
        </Panel>

        {result && (
          <Panel>
            <PanelHeader title="Result" />
            <div className="p-4 space-y-2">
              {result.matched && result.route ? (
                <div className="flex items-center gap-2">
                  <Pill tone="success">matched</Pill>
                  <span className="text-sm font-medium text-fg">{result.route.name}</span>
                  <PersonaBadge name={result.route.persona} />
                  <AclBadge acl={result.route.acl.inbound as InboundACL} />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Pill tone="warning">no match</Pill>
                  <span className="text-xs text-fg-muted">
                    falls through to default ACL{result.route?.acl ? `: ${result.route.acl.inbound}` : ""}
                  </span>
                </div>
              )}
            </div>
          </Panel>
        )}

        {cfg && (
          <Panel>
            <PanelHeader title="Current routes" subtitle="Evaluated top-down; first match wins." />
            <ul className="divide-y divide-border">
              {cfg.routes.map((r) => (
                <li key={r.name} className="px-4 py-2 flex items-center gap-2 text-sm">
                  {r.match.platform && <PlatformBadge platform={r.match.platform as Platform} compact />}
                  <span className="font-medium text-fg">{r.name}</span>
                  <AclBadge acl={r.acl.inbound as InboundACL} />
                  <PersonaBadge name={r.persona ?? r.sink_type ?? "—"} />
                  <span className="ml-auto font-mono text-xs text-fg-subtle truncate max-w-[14rem]">
                    {r.match.chat_jid ?? "—"}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </PageBody>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">{label}</span>
      {children}
    </label>
  );
}
